import { InjectionToken, NgModule, inject, provideAppInitializer } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { IPublicClientApplication, PublicClientApplication, BrowserCacheLocation, LogLevel } from '@azure/msal-browser';
import {
    MsalGuard,
    MsalInterceptor,
    MsalBroadcastService,
    MsalInterceptorConfiguration,
    MsalModule,
    MsalService,
    MSAL_GUARD_CONFIG,
    MSAL_INSTANCE,
    MSAL_INTERCEPTOR_CONFIG,
    MsalGuardConfiguration
} from '@azure/msal-angular';

import { EnvConfigLoaderService } from '../shared/services/app';
import { interceptorAuthRequest, scopeAccountsToAuthority } from './auth-mode';

const isIE =
    window.navigator.userAgent.indexOf('MSIE ') > -1 ||
    window.navigator.userAgent.indexOf('Trident/') > -1;

const AUTH_CONFIG_URL_TOKEN = new InjectionToken<string>('AUTH_CONFIG_URL');

export function initializerFactory(config: EnvConfigLoaderService, configUrl: string): any {
    const promise = config.init(configUrl).then((_value) => {
        if (!config.getSettings().production) {
              console.log('finished getting configurations dynamically.');
        }
    });
    return () => promise;
}

export function MSALInstanceFactory(config: EnvConfigLoaderService): IPublicClientApplication {
    const instance = new PublicClientApplication({
        auth: config.getSettings('msal').auth,
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            storeAuthStateInCookie: isIE
        },
        system: {
            loggerOptions: {
                loggerCallback(logLevel: LogLevel, message: string) {
                    if (!config.getSettings().production) {
                        console.log(message);
                    }
                },
                logLevel: LogLevel.Verbose,
                piiLoggingEnabled: false
            }
        }
    });
    // B2C and External ID accounts share one cache on dev; each app only sees its own (ZEY-3142).
    scopeAccountsToAuthority(instance, config.getSettings());
    return instance;
}

export function MSALInterceptorConfigFactory(config: EnvConfigLoaderService): MsalInterceptorConfiguration {
    const protectedResourceMap = new Map<string, Array<string>>(config.getSettings('interceptor').protectedResourceMap);

    return {
        interactionType: config.getSettings('interceptor').interactionType,
        protectedResourceMap,
        // The External ID page opens with the last B2C email filled in (ZEY-3142).
        authRequest: interceptorAuthRequest(config.getSettings('guard')?.authRequest?.loginHint)
    };
}

export function MSALGuardConfigFactory(config: EnvConfigLoaderService): MsalGuardConfiguration {
    return {
        interactionType: config.getSettings('guard').interactionType,
        authRequest: config.getSettings('guard').authRequest
    };
}

@NgModule({
    providers: [],
    imports: [MsalModule]
})
export class AuthenticationDynamicModule {
    static forRoot(configFile: string) {
        return {
            ngModule: AuthenticationDynamicModule,
            providers: [
                EnvConfigLoaderService,
                { provide: AUTH_CONFIG_URL_TOKEN, useValue: configFile },
                provideAppInitializer(() => {
        const initializerFn = (initializerFactory)(inject(EnvConfigLoaderService), inject(AUTH_CONFIG_URL_TOKEN));
        return initializerFn();
      }),
                {
                    provide: MSAL_INSTANCE,
                    useFactory: MSALInstanceFactory,
                    deps: [EnvConfigLoaderService]
                },
                {
                    provide: MSAL_GUARD_CONFIG,
                    useFactory: MSALGuardConfigFactory,
                    deps: [EnvConfigLoaderService]
                },
                {
                    provide: MSAL_INTERCEPTOR_CONFIG,
                    useFactory: MSALInterceptorConfigFactory,
                    deps: [EnvConfigLoaderService]
                },
                MsalService,
                MsalGuard,
                MsalBroadcastService,
                {
                    provide: HTTP_INTERCEPTORS,
                    useClass: MsalInterceptor,
                    multi: true
                }
            ]
        };
    }
}
